import React from "react";

import { BlogPostListItemSubBlock } from "@reactivated";

import { ImageChooserBlock } from "@thelabnyc/thelabui/src/components/ImageChooserBlock";
import { VideoChooserBlock } from "@thelabnyc/thelabui/src/components/VideoChooserBlock";
import { concatClassNames } from "@thelabnyc/thelabui/src/utils/styles";

import { Mask, revealClassName, useReveal } from "../../shared/Reveal";
import { usePrefersReducedMotion } from "../../shared/reducedMotion";

import styles from "./index.module.scss";

/** Card slots, in CMS order: right column top, under the header, right column below */
const SLOT_CLASSES = [styles.card1, styles.card2, styles.card3];

// The image is one grid column on desktop, two at tablet, the page on a phone
const IMAGE_SIZES = [
    "(max-width: 767px) calc(100vw - 40px)",
    "(max-width: 1023px) calc((100vw - 64px) / 2)",
    "calc((100vw - 80px) / 4)",
].join(", ");

/**
 * Plays an ambient loop only while it is on screen (or about to be), and holds
 * it on its first frame under reduced motion. No rewind on pause.
 */
const useAmbientVideo = () => {
    const ref = React.useRef<HTMLVideoElement>(null);
    const reduced = usePrefersReducedMotion();

    React.useEffect(() => {
        const video = ref.current;
        if (!video) return;
        if (reduced) {
            video.removeAttribute("autoplay");
            video.pause();
            return;
        }
        const observer = new IntersectionObserver(
            (entries) => {
                const entry = entries[entries.length - 1];
                if (entry.isIntersecting) {
                    // play() rejects if a fast scroll pauses it first
                    if (video.paused) video.play().catch(() => {});
                } else if (!video.paused) {
                    video.pause();
                }
            },
            { threshold: 0, rootMargin: "120px" },
        );
        observer.observe(video);
        return () => observer.disconnect();
    }, [reduced]);

    return { ref, reduced };
};

type MediaProps = Pick<BlogPostListItemSubBlock, "media">;

const Media = (props: MediaProps) => {
    const { ref: videoRef, reduced } = useAmbientVideo();
    const item = props.media?.[0];
    if (item?.type === "image") {
        return (
            <ImageChooserBlock
                value={item.value}
                sizes={IMAGE_SIZES}
                attrs={{ className: styles.picture }}
                imageAttrs={{ alt: "" }}
            />
        );
    }
    if (item?.type === "video") {
        return (
            <VideoChooserBlock
                ref={videoRef}
                value={item.value}
                iOSFriendlyMutedAutoPlay={true}
                attrs={{
                    "autoPlay": !reduced,
                    "loop": true,
                    "preload": "auto",
                    "aria-hidden": true,
                }}
            />
        );
    }
    return null;
};

interface Props {
    value: BlogPostListItemSubBlock;
    /** 0, 1 or 2: the card's slot and its place in the stagger */
    index: number;
    onImageEnter: () => void;
    onImageLeave: () => void;
}

/**
 * One case study: the image or loop, client, title and the first two tags.
 * The frame opens outward from its centre, then the copy follows line by line.
 */
export const Card = (props: Props) => {
    const d = props.index * 110;
    const { ref, shown } = useReveal<HTMLAnchorElement>({
        enter: 0.86,
        delays: [d, d + 60, d + 420, d + 530, d + 640],
    });
    const [card, frame, client, title, tags] = shown;

    return (
        <a
            ref={ref}
            href={props.value.url}
            className={concatClassNames([
                styles.card,
                SLOT_CLASSES[props.index],
                revealClassName(card),
                card ? "" : styles.armed,
            ])}
        >
            <article className={styles.article}>
                <div
                    className={concatClassNames([
                        styles.image,
                        frame ? "" : styles.framed,
                    ])}
                    onPointerEnter={props.onImageEnter}
                    onPointerLeave={props.onImageLeave}
                >
                    <Media media={props.value.media} />
                </div>
                <div className={styles.meta}>
                    <p className={styles.client}>
                        <Mask shown={client}>{props.value.client}</Mask>
                    </p>
                    <h3 className={styles.title}>
                        <Mask shown={title}>{props.value.title}</Mask>
                    </h3>
                    {props.value.tags.length > 0 && (
                        <Mask shown={tags} className={styles.pills}>
                            {props.value.tags.slice(0, 2).map((tag) => (
                                <span key={tag.id} className={styles.pill}>
                                    {tag.name}
                                </span>
                            ))}
                        </Mask>
                    )}
                </div>
            </article>
        </a>
    );
};
